<template>
  <div class="page-wall">
    <div class="uk-grid-medium" uk-grid>
      <div class="uk-width-5-5">

        <PageHeader :title="wallTitle" icon="uil-coffee"/>
        <div class="uk-grid-medium" uk-grid>
          <div class="uk-width-1-4@l uk-width-1-3@m uk-width-1-1@s wall-left-col">
            <div uk-sticky="offset:100; media:@l">
              <WallProfileSection :currentUser="current_user" :latestActions="latest_actions"/>
            </div>
          </div>

          <div class="uk-width-1-2@l uk-width-2-3@m uk-width-1-1@s wall-center-col">
            <!--- Create Post Section -->
            <div v-if="!userId" class="card gedf-card wall-create-card">
              <div class="card-body">
                <template v-if="errorMessages">
                  <div v-for="(error, index) in errorMessages" :key="index" class="uk-alert-danger mb-2" uk-alert>
                    <a class="uk-alert-close" uk-close></a>
                    <p style="color: red">
                      <i class="uil-exclamation-triangle"></i> {{ error }}
                    </p>
                  </div>
                </template>
                <div v-if="successMessage" class="uk-alert-success mb-3" uk-alert>
                  <a class="uk-alert-close" uk-close></a>
                  <p style="color: green">
                    <i class="uil-exclamation-triangle"></i>
                    {{ successMessage }}
                  </p>
                </div>
                <div class="wall-create-top">
                  <UserProfileAvatar
                    :name="[current_user?.name, current_user?.surname].filter(Boolean).join(' ')"
                    :photo="current_user?.photo_link"
                    class="wall-avatar"
                  />
                  <div class="wall-create-input-wrapper">
                    <textarea ref="postTextarea" v-model="form.text"
                              :placeholder="$t('general.share_a _comment')" class="wall-create-input" rows="1"
                              @click="rememberPostTextSelection" @input="rememberPostTextSelection"
                              @keyup="rememberPostTextSelection" @select="rememberPostTextSelection"/>
                  </div>
                </div>

                <div class="wall-create-actions">
                  <div class="btn-toolbar justify-content-between w-100">
                    <WallEmojiPicker @open="rememberPostTextSelection" @select="insertPostEmoji"/>
                    <div class="btn-group ml-auto">
                      <button class="btn btn-outline-primary btn-sm wall-btn-ghost" type="button"
                              @click="$refs.myFiles.click()">
                        <i aria-hidden="true" class="uil uil-image"></i>
                        <span class="wall-multimedia-label">
                          {{ addMultimediaText }}
                          <template v-if="files.length">({{ files.length }})</template>
                        </span>
                      </button>
                      <button :disabled="isSavingPost" class="btn btn-primary btn-sm wall-btn-solid" type="button"
                              @click="savePost">
                        <i class="uil uil-message mr-1"></i>
                        {{ $t("general.share") }}
                      </button>
                    </div>
                  </div>
                </div>

                <div v-if="selectedChannel" class="btn-primary wall-channel-warning-compact mt-2">
                  <i class="uil uil-hash"></i>
                  <span>{{ $t("general.your_post") }} <strong>#{{
                      selectedChannel.name
                    }}</strong> {{ $t("general.share_on_ the_channel") }}</span>
                </div>

                <div v-if="files.length" class="wall-files mt-3">
                  <ul class="uk-list-divider pl-1 pr-1">
                    <li v-for="(file, index) in files" :key="'file_' + index"
                        class="d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center">
                        <i class="uil-trash text-danger mr-2" style="cursor: pointer; font-size: 18px"
                           @click="removeFile(index)"></i>
                        <span class="text-left text-secondary">
                          {{ file.name }}
                        </span>
                      </div>
                      <span class="wall-file-meta">
                        <i :class="file.type.includes('image')
                          ? 'uil-image'
                          : 'uil-video'
                          " class="mr-1"/>
                        {{ bytesToSize(file.size) }}
                      </span>
                    </li>
                  </ul>
                </div>

                <input id="customFile" ref="myFiles" accept="video/*,image/*" class="d-none" multiple type="file"
                       @change="handlePostMediaSelection"/>
              </div>
            </div>

            <div class="wall-filters-wrapper">
              <input v-model="filter.search" :placeholder="$t('general.search_in_wall')" class="wall-search-input"
                     type="text" @keyup="filterSocialPosts"/>

              <div class="wall-sort-multiselect">
                <multiselect v-model="sortOptionModel" :allow-empty="false" :maxHeight="200" :openDirection="'bottom'"
                             :options="sortOptions" :placeholder="$t('general.select')" :searchable="false"
                             deselectLabel=""
                             label="name" selectLabel="" selectedLabel="" track-by="id"/>
              </div>
            </div>

            <!-- Loading Skeletons -->
            <single-post-skeleton v-if="posts_loading && $route.params.id"/>
            <wall-posts-skeleton v-else-if="posts_loading" :count="3"/>

            <template v-if="!posts_loading">
              <template v-for="(post, index) in formattedPosts">
                <div v-if="post" :id="'wall-post-' + post.id"
                     class="uk-card uk-card-default rounded mb-4 wall-post-card">
                  <div class="uk-card-header wall-post-header">
                    <div class="uk-grid-small uk-flex-middle" uk-grid>
                      <div class="uk-width-auto">
                        <UserProfileAvatar
                          :name="[post.user?.name, post.user?.surname].filter(Boolean).join(' ')"
                          :photo="post.user?.photo_link"
                          class="wall-avatar"
                        />
                      </div>
                      <div class="uk-width-expand">
                        <h5 class="h6 mt-0 mb-0">
                          {{ post.user?.name + " " + post.user?.surname }}
                        </h5>
                        <p class="uk-margin-remove-top text-black-50 uk-small">
                          @{{
                            (
                                post.user?.name +
                                "." +
                                post.user?.surname
                            ).toLowerCase()
                          }}
                        </p>
                      </div>
                      <div class="uk-width-auto">
                        <div class="text-muted h7 mb-0 wall-post-date">
                          <i class="uil-clock mr-1"></i>
                          {{ timeFromNow(post.created_at) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="uk-card-body wall-post-body">
                    <p class="mb-3 wall-post-text" v-text="formatPostText(post.text)"></p>
                    <p v-if="post.status_id === 1" class="mb-3 ml-2 wall-post-status">
                      <strong>
                        {{ $t("general.sent_to_admin_approval") }}
                      </strong>
                    </p>

                    <div v-if="getPostFiles(post).length" :class="getPostMediaClass(post)" class="wall-post-media"
                         uk-slider @beforeitemshow="pausePostVideos" @itemshow="pausePostVideos">
                      <div class="uk-position-relative">
                        <div class="uk-slider-container wall-post-slider-container">
                          <ul class="uk-slider-items wall-post-slider-items">
                            <li v-for="file in getPostFiles(post)" :key="'post_file_item_' + post?.id + '_' + file.id"
                                class="wall-post-media-item">
                              <img v-if="isImageFile(file)" :alt="file.name" :data-src="file.file_link"
                                   class="wall-post-image" uk-img @click="openImagePreview(file.file_link)"/>
                              <video v-if="isVideoFile(file)" :src="file.file_link" class="wall-post-video" controls/>
                            </li>
                          </ul>
                        </div>

                        <template v-if="getPostFiles(post).length > 1">
                          <a class="wall-post-slider-nav wall-post-slider-nav--prev" href="#" uk-slider-item="previous"
                             @click.prevent="pausePostVideos">
                            <i class="uil uil-angle-left"></i>
                          </a>
                          <a class="wall-post-slider-nav wall-post-slider-nav--next" href="#" uk-slider-item="next"
                             @click.prevent="pausePostVideos">
                            <i class="uil uil-angle-right"></i>
                          </a>
                        </template>
                      </div>
                    </div>
                  </div>

                  <div class="wall-post-actions">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="wall-post-action-buttons">
                        <button :class="{ 'wall-like-btn--active': post.user_liked_count }"
                                class="btn btn-sm wall-like-btn" @click.prevent="likePost(post.id, index)">
                          <i class="uil-thumbs-up mr-1"></i>
                          <span v-if="!post.user_liked_count">
                            {{ $t("general.like") }}
                          </span>
                          <span v-else>
                            {{ $t("general.dont_like") }}
                          </span>
                        </button>

                        <button class="btn btn-sm wall-comment-btn" @click="openCommentArea(post.id, index)">
                          <i class="uil-comment mr-1"></i>
                          {{ $t("general.make_comment") }}
                        </button>
                      </div>

                      <div class="wall-post-meta d-flex align-items-center">
                        <a class="mr-3" href="javascript:void(0)" @click="showLiked(post)">
                          <i class="uil-heart mr-1"></i>
                          {{ post.likes_count }}
                        </a>
                        <a v-if="getCommentsTotal(post) > 0" class="wall-post-comments-count" href="javascript:void(0)"
                           @click.prevent="toggleCommentsVisibility(post, index)">
                          <i class="uil-comment mr-1"></i>
                          {{ post.comments_count }}
                          {{ $t("assignment.comment") }}
                        </a>
                        <span v-else>
                          <i class="uil-comment mr-1"></i>
                          {{ post.comments_count }}
                          {{ $t("assignment.comment") }}
                        </span>
                        <a v-if="post.user?.id == current_user?.id" class="ml-3 wall-post-delete"
                           href="javascript:void(0)" @click="deletePost(post.id)">
                          <i class="uil-trash-alt"></i>
                        </a>
                      </div>
                    </div>

                    <div v-if="activeCommentPostId === post.id" class="wall-post-comment-form mt-3">
                      <textarea :ref="'comment_textarea_' + post.id + '_' + index" v-model="form.comment"
                                :placeholder="$t('general.what_are_you_thinking')" class="wall-post-comment-textarea"
                                rows="2"/>
                      <div class="d-flex justify-content-end">
                        <button v-if="form.comment.length" class="btn btn-sm btn-primary wall-post-comment-btn"
                                type="button" @click="commentPost(post.id, index)">
                          {{ $t("general.save") }}
                        </button>
                      </div>
                    </div>

                    <template v-if="isCommentsVisible(post.id) && getCommentsTotal(post) > 0">
                      <hr/>
                      <ul v-if="getVisibleComments(post).length"
                          class="wall-post-comments-list comment_tree_view parent_comment_tree_view">
                        <li v-for="(comment, comment_index) in getVisibleComments(post)"
                            :key="'comment_item_' + comment.id + '_' + comment_index">
                          <CommentItem :comment="comment" :current_user="current_user" :post_id="post.id"
                                       :post_index="index" @reply="replyComment"
                                       @reset="resetSocialPosts"></CommentItem>
                        </li>
                      </ul>
                      <button v-if="shouldShowCommentsToggle(post)" class="wall-comments-toggle-btn" type="button"
                              @click="toggleComments(post, index)">
                        {{
                          isCommentsExpanded(post.id)
                              ? ($t("general.show_less"))
                              : ($t("general.show_more_comments"))
                        }}
                      </button>
                    </template>
                  </div>
                </div>
              </template>

              <template v-if="!formattedPosts.length">
                <div class="row text-center">
                  <div class="col-12">
                    <div class="uk-alert-success bg-success-700 h-25px" uk-alert>
                      <p style="color: green">
                        {{ $t("general.there_is_no_sharing_yet") }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </template>

            <!-- PAGINATION -->
            <pagination v-if="pagination && pagination.hasOwnProperty('links') && pagination.last_page > 1"
                        :pagination="pagination" @changePage="setPage($event)"/>
          </div>

          <div class="uk-width-1-4@l uk-width-1-1@m uk-width-1-1@s wall-right-col">
            <div uk-sticky="offset:100; media:@l">
              <div class="card wall-channel-card">
                <div class="card-header d-flex justify-content-between align-items-center py-2">
                  <h5 class="h6 mb-0">
                    {{ $t("general.channels") }}
                  </h5>

                  <button v-if="canCreateChannel" :title="$t('general.add_channel')"
                          class="btn btn-sm btn-light wall-channel-add-btn" type="button" @click="openNewChannelModal">
                    <i class="uil uil-plus"></i>
                  </button>
                </div>

                <div class="py-3 wall-channel-body">
                  <div v-if="sortedChannels && sortedChannels.length" class="wall-channels-list">
                    <div v-for="(channel, cIndex) in channelList"
                         :key="channel.id || channel.slug || channel.name || cIndex" :class="{
                        'wall-channel-item--active btn-primary': isChannelSelected(channel),
                        'wall-channel-item--pending': isChannelPending(channel)
                      }" class="wall-channel-item" @click="selectChannel(channel)">
                      <div class="wall-channel-icon">
                        <i class="uil uil-hash"></i>
                      </div>
                      <div class="wall-channel-info">
                        <span class="wall-channel-name">
                          {{ channel.id === null ? $t("Sosyal Duvarım") : "#" + (channel.name || channel.title) }}
                        </span>
                        <span v-if="isChannelNew(channel)" class="wall-channel-new-badge btn-primary">
                          {{ $t("general.new") }}
                        </span>
                        <span v-if="isChannelPending(channel)"
                              :uk-tooltip="'title: ' + $t('general.sent_to_admin_approval') + '; pos: bottom'"
                              class="wall-channel-pending-info btn-danger">
                          <i class="uil uil-exclamation-triangle"></i>
                        </span>
                        <span v-if="channel.is_private || channel.type === 2" class="wall-channel-private">
                          <i class="uil uil-lock"></i>
                        </span>
                      </div>
                      <div v-if="isCompanyAdmin && channel.id !== null"
                           class="wall-channel-actions wall-channel-actions--visible" @click.stop>
                        <button :disabled="isChannelPending(channel)" :title="$t('general.edit')"
                                class="wall-channel-action-btn" type="button" @click="editChannel(channel)">
                          <i class="uil uil-pen"></i>
                        </button>
                        <button :disabled="isChannelPending(channel)" :title="$t('general.delete')"
                                class="wall-channel-action-btn wall-channel-action-btn--danger" type="button"
                                @click="deleteChannel(channel)">
                          <i class="uil uil-multiply"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <p v-else class="mb-0 text-muted wall-channel-empty">
                    {{ $t('general.no_channel') }}
                  </p>

                  <!-- View All Posts Button -->
                  <button v-if="$route.params.id" class="wall-view-all-btn" type="button" @click="goToFullWall">
                    <i class="uil uil-apps"></i>
                    <span>{{ viewAllPostsText }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <LikedListModal :likedList="likedList"/>

    <ImageRatioModal
        ref="imageRatioModal"
        :applying="isApplyingImageCrop"
        :image-edit-preview-url="activeImageCropPreviewUrl"
        :image-natural-height="activeImageNaturalHeight"
        :image-natural-width="activeImageNaturalWidth"
        :visible="isImageRatioModalVisible"
        @apply="applySelectedImageAspect"
        @close="cancelImageCropping"
        @use-original="keepOriginalImage"
    />

    <ChannelModal ref="channelModal" :criterionMatchedCount="criterionMatchedCount" :editingChannel="editingChannel"
                  :isSaving="isSavingChannel" :memberCriterionLoading="memberCriterionLoading"
                  :memberCriterionTypeOptions="memberCriterionTypeOptions"
                  :memberCriterionValueOptions="memberCriterionValueOptions"
                  :users="users" :usersLoading="usersLoading" @save="handleSaveChannel"
                  @criterion-type-change="handleCriterionTypeChange"
                  @criterion-value-change="handleCriterionValueChange"
                  @apply-criterion="handleApplyCriterion"/>

    <div v-if="imagePreviewUrl" class="wall-image-viewer" @click.self="closeImagePreview">
      <button class="wall-image-viewer-close" type="button" @click="closeImagePreview">
        <i class="uil uil-times"></i>
      </button>
      <img :src="imagePreviewUrl" alt="image-preview" class="wall-image-viewer-media"/>
    </div>
  </div>
</template>

<script>
import store from "@/core/services";
import moment from "moment";
import 'moment/locale/fr';
import 'moment/locale/es';
import 'moment/locale/az';
import 'moment/locale/tr';
import 'moment/locale/en-gb';

import module, {
  BASE_URL,
  MODULE_NAME,
  ERROR,
  ITEMS,
  GET_ITEMS,
  CREATE_ITEM,
  ITEM,
  LOADING,
  SET_ERROR,
} from "@/core/services/store/social_post.module";
import user_module, {
  MODULE_NAME as USER_MODULE_NAME,
  GET_USER,
  CURRENT_ITEM as CURRENT_USER,
} from "@/core/services/store/user.module";
import {
  CREATE_ITEM as REST_CREATE_ITEM,
  GET_ITEMS as REST_GET_ITEMS,
  DELETE_ITEM_BY_ID,
} from "@/core/services/store/REST.module";
import social_channel_module, {
  MODULE_NAME as SOCIAL_CHANNEL_MODULE_NAME,
} from "@/core/services/store/social_channel.module";
import Pagination from "@/assets/components/Pagination.vue";
import CommentItem from "@/view/components/wall/Comment.vue";
import axios from "axios";
import SecureLS from "secure-ls";
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import { confirmDialogDelete } from "@/utils/confirmDialog";
import { cropImageByAspect, IMAGE_ASPECT_RATIOS } from "@/utils/imageCrop";
import Swal from "sweetalert2";
import wallMixin from "@/view/User/mixin/wallMixin";
import company_module, {
  MODULE_NAME as COMPANY_MODULE_NAME,
  SELF as COMPANY_SELF,
} from "@/core/services/store/company.module";
import LikedListModal from "./components/LikedListModal.vue";
import ImageRatioModal from "./components/ImageRatioModal.vue";
import ChannelModal from "./components/ChannelModal.vue";
import WallProfileSection from "./components/WallProfileSection.vue";
import WallPostsSkeleton from "./components/WallPostsSkeleton.vue";
import SinglePostSkeleton from "./components/SinglePostSkeleton.vue";
import WallEmojiPicker from "./components/WallEmojiPicker.vue";
import { postCreationErrorMessage } from "@/view/User/Widgets/SocialWall/utils/apiPayload";

const { emojifyText, insertTextAtSelection, } = require("@/utils/emojiUtils");

const ls = new SecureLS({ encodingType: "aes", isCompression: true });

export default {
  name: "Wall",
  mixins: [wallMixin],
  components: {
    Pagination,
    CommentItem,
    UserProfileAvatar,
    PageHeader,
    LikedListModal,
    ImageRatioModal,
    ChannelModal,
    WallProfileSection,
    WallPostsSkeleton,
    SinglePostSkeleton,
    WallEmojiPicker,
  },
  props: {
    id: {
      required: false,
      default: null,
    },
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, module);
    registerStoreModule(USER_MODULE_NAME, user_module);
    registerStoreModule(SOCIAL_CHANNEL_MODULE_NAME, social_channel_module);
    registerStoreModule(COMPANY_MODULE_NAME, company_module);
  },
  data() {
    return {
      likedList: [],
      pagination: null,
      successMessage: null,
      isSavingPost: false,
      isSavingChannel: false,
      files: [],
      userId: null,
      errorMessages: [],
      userSelfURL: "api/user/self",
      userShowURL: "api/user/",
      likeURL: "api/social-post/like/",
      commentURL: "api/social-post/comment/",
      form: {
        multi_media: true,
        text: "",
        comment: "",
      },
      filter: {
        page: 1,
        per_page: 10,
        search: null,
        order: "desc",
        sort: "id",
        id: null,
      },
      latest_actions: [],
      menuList: [],

      channels: [],
      selectedChannel: null,
      newChannel: {
        name: "",
        is_private: false,
        selected_members: [],
      },
      editingChannel: null,
      channelListURL: "api/social/channels",
      channelCreateURL: "api/social/channels",
      usersListURL: "api/user",
      users: [],
      usersLoading: false,
      memberCriterionType: "position",
      memberCriterionValue: null,
      memberCriterionValues: [],
      memberCriterionLoading: false,
      channelEditRequestKey: 0,
      channelModalEl: null,
      isImageRatioModalVisible: false,
      imagePreviewUrl: null,
      pendingImageCropFiles: [],
      activeImageCropFile: null,
      activeImageCropPreviewUrl: null,
      activeImageNaturalWidth: 0,
      activeImageNaturalHeight: 0,
      isApplyingImageCrop: false,
      imageCropSessionId: 0,
      activeCommentPostId: null,
      visibleComments: {},
      expandedComments: {},
      postTextSelectionStart: 0,
      postTextSelectionEnd: 0,
    };
  },
  computed: {
    channelList() {
      return [
        {
          id: null,
          // name: this.$t("general.all_channels")
          name: 'Sosyal Duvarım'

        },
        ...(this.sortedChannels || [])
      ];
    },
    activeLanguageCode() {
      return String((this.$i18n && this.$i18n.locale) || this.secureLocalStorage("language") || "tr").toLowerCase();
    },
    company: {
      get() {
        return store.getters[COMPANY_MODULE_NAME + "/" + COMPANY_SELF];
      },
      set(value) {
      },
    },
    wallTitle() {
      const sourceMenu = this.getDashboardMenuItems(this.menuList);
      const menuItem = sourceMenu.find((item) => item.type === "wall");
      return this.getLocalizedWidgetTitle(menuItem, this.$t("home.Wall"), this.$i18n?.locale);
    },
    posts: {
      get() {
        return store.getters[MODULE_NAME + "/" + ITEMS];
      },
      set(value) {
      },
    },
    posts_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set(value) {
      },
    },
    formattedPosts() {
      let items = this.posts;
      if (items === null || !("data" in items) || !items.data.length) return [];
      this.pagination = items;
      return items.data;
    },
    post: {
      get() {
        return store.getters[MODULE_NAME + "/" + ITEM];
      },
      set(value) {
      },
    },
    current_user: {
      get() {
        let item = this.$store.getters[USER_MODULE_NAME + "/" + CURRENT_USER];
        if (item && this.userId) return item.item;
        return item;
      },
      set(val) {
      },
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set(value) {
      },
    },
    sortOptions() {
      return [
        { id: "id", name: this.$t("general.sort_latest") },
        { id: "likes_count", name: this.$t("general.sort_most_liked") },
        { id: "comments_count", name: this.$t("general.sort_most_commented") },
      ];
    },
    sortOptionModel: {
      get() {
        return this.sortOptions.find((item) => item.id === this.filter.sort) || this.sortOptions[0];
      },
      set(value) {
        if (!value?.id) return;
        if (this.filter.sort === value.id) return;
        this.filter.sort = value.id;
        this.filter.page = 1;
        this.filterSocialPosts();
      },
    },
    memberCriterionTypeOptions() {
      return this.memberCriterionOptions.map((option) => ({
        id: option.id,
        name: this.getMemberCriterionLabel(option),
      }));
    },
    memberCriterionValueOptions() {
      if (!Array.isArray(this.memberCriterionValues)) return [];
      return this.memberCriterionValues
          .filter((value) => value && value.id !== undefined && value.id !== null)
          .map((value) => ({
            id: String(value.id),
            name: value.name,
          }));
    },
    criterionMatchedUsers() {
      if (!this.memberCriterionValue || !Array.isArray(this.users) || !this.users.length) {
        return [];
      }
      return this.users.filter((user) => {
        const values = this.getUserCriterionValues(user, this.memberCriterionType);
        return values.includes(String(this.memberCriterionValue));
      });
    },
    criterionMatchedCount() {
      return this.criterionMatchedUsers.length;
    },
    isCompanyAdmin() {
      try {
        const userStr = ls.get("user");
        if (!userStr) {
          return false;
        }

        const user = JSON.parse(userStr);

        const roles = user?.roles || [];
        const isAdmin = roles.some(r =>
            r?.name === 'company_admin' || r?.name === 'admin'
        );

        return isAdmin;
      } catch (error) {
        const userRoles = this.current_user?.roles || [];
        const isAdmin = userRoles.some(r =>
            r?.name === 'company_admin' || r?.name === 'admin'
        );

        return isAdmin;
      }
    },

    canCreateChannel() {
      if (this.isCompanyAdmin) return true;

      const settings = this.company?.settings || [];
      const setting = settings.find(x => x.key === 'social_channels_user_can_create');

      const canCreate = setting !== undefined ? (setting.value === true || setting.value === 1 || setting.value === "1" || setting.value === "true") : true;

      return canCreate;
    },
    sortedChannels() {
      if (!this.channels || !this.channels.length) return [];
      const channels = this.isCompanyAdmin
          ? [...this.channels]
          : this.channels.filter((channel) => this.isChannelApproved(channel));

      return channels.sort((a, b) => {
        const nameA = (a.name || a.title || '').toLowerCase();
        const nameB = (b.name || b.title || '').toLowerCase();
        return nameA.localeCompare(nameB, 'tr');
      });
    },
    viewAllPostsText() {
      return this.$t("general.see_all_posts");
    },
    addMultimediaText() {
      const translationKey = "general.add_multimedia";
      const translatedText = this.$t(translationKey);

      return translatedText && translatedText !== translationKey
          ? translatedText
          : "Multimedya ekle";
    },
  },
  methods: {
    formatPostText(text) {
      return emojifyText(text);
    },
    rememberPostTextSelection() {
      const textarea = this.$refs.postTextarea;
      const textLength = String(this.form.text || "").length;

      if (!textarea) {
        this.postTextSelectionStart = textLength;
        this.postTextSelectionEnd = textLength;
        return;
      }

      this.postTextSelectionStart = Number.isInteger(textarea.selectionStart)
          ? textarea.selectionStart
          : textLength;
      this.postTextSelectionEnd = Number.isInteger(textarea.selectionEnd)
          ? textarea.selectionEnd
          : this.postTextSelectionStart;
    },
    insertPostEmoji(emoji) {
      if (!emoji) return;

      const insertion = insertTextAtSelection(
          this.form.text,
          emoji,
          this.postTextSelectionStart,
          this.postTextSelectionEnd
      );

      this.form.text = insertion.text;
      this.postTextSelectionStart = insertion.cursor;
      this.postTextSelectionEnd = insertion.cursor;

      this.$nextTick(() => {
        const textarea = this.$refs.postTextarea;
        if (!textarea) return;

        textarea.focus();
        textarea.setSelectionRange(insertion.cursor, insertion.cursor);
      });
    },
    showLiked(post) {
      axios
          .get(this.likeURL + post.id + "/show", {
            headers: this.apiHeaders,
          })
          .then((response) => {
            this.likedList = response.data;
            UIkit.modal("#modal_likedList").show();
          });
    },
    openImagePreview(url) {
      if (!url) return;
      this.imagePreviewUrl = url;
    },
    closeImagePreview() {
      this.imagePreviewUrl = null;
    },
    pausePostVideos(event) {
      const mediaRoot = event?.currentTarget?.closest
          ? event.currentTarget.closest(".wall-post-media")
          : event?.target?.closest?.(".wall-post-media");
      const videos = mediaRoot
          ? Array.from(mediaRoot.querySelectorAll("video"))
          : [];

      videos.forEach((video) => {
        if (!video.paused) {
          video.pause();
        }
      });
    },
    getPostFiles(post) {
      return Array.isArray(post?.files) ? post.files : [];
    },
    getPostMediaClass(post) {
      const count = this.getPostFiles(post).length;
      return {
        "wall-post-media--single": count === 1,
        "wall-post-media--slider": count > 1,
      };
    },
    isImageFile(file) {
      return Number(file?.file_type) === 1;
    },
    isVideoFile(file) {
      return Number(file?.file_type) === 2;
    },
    onImagePreviewKeydown(event) {
      if (event?.key === "Escape" && this.imagePreviewUrl) {
        this.closeImagePreview();
      }
    },
    deletePost(id) {
      this.$store
          .dispatch(DELETE_ITEM_BY_ID, {
            url: "/api/social-post/" + id,
            acceptPromise: true,
          })
          .then(() => {
            this.filterSocialPosts();
            this.getLatestActions();
          });
    },
    getLatestActions() {
      let url = this.userId
          ? "api/social-post/latest-actions/" + this.userId
          : "api/social-post/latest-actions";
      this.$store
          .dispatch(REST_GET_ITEMS, {
            url: url,
            acceptPromise: true,
          })
          .then((response) => {
            this.latest_actions = [];

            if (typeof response.data !== undefined) {
              response.data.forEach((item) => {
                let message = "";

                switch (item.data_type) {
                  case "likes":
                    let likeLabel =
                        item.text && item.text.trim() !== null
                            ? `"${item.text}" adlı`
                            : "Bir";
                    message = likeLabel + " " + this.$t("general.liked_post");
                    break;
                  case "social_posts":
                    let postLabel =
                        item.text && item.text.trim() !== null
                            ? `"${item.text}" adlı`
                            : "Bir";
                    message = postLabel + " " + this.$t("general.shared_post");
                    break;
                  case "comments":
                    message = "Bir " + this.$t("general.commented_post");
                    break;
                }

                item.message = message;
                this.latest_actions.push(item);
              });
            }
          });
    },
    getCurrentUser() {
      let url = this.userId
          ? this.userShowURL + this.userId
          : this.userSelfURL;
      this.$store.dispatch(USER_MODULE_NAME + "/" + GET_USER, {
        url: url,
        acceptPromise: true,
      });
    },
    resetSocialPosts() {
      this.filterSocialPosts();
      this.getLatestActions();
    },
    filterSocialPosts(id = null) {
      const filters = {
        like: this.filter.search,
        page: this.filter.page,
        per_page: this.filter.per_page,
        sort: this.filter.sort,
        order: this.filter.order,
        id: id,
        user_id: this.userId,
      };

      if (this.selectedChannel) {
        filters.social_channel_id = this.selectedChannel.id;
      } else if (!this.isCompanyAdmin) {
        filters.only_general = 1;
      }

      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: filters,
      });
    },
    savePost() {
      if (this.isSavingPost) return;
      let form = this.form;
      let formData = new FormData();
      this.errorMessages = [];
      if (form.text.length < 3) {
        this.errorMessages.push(
            this.$t("validation.required_field").replace(
                ":NAME",
                this.$t("post.text")
            )
        );
        return false;
      }
      formData.append("text", emojifyText(form.text));

      if (this.selectedChannel) {
        formData.append("social_channel_id", this.selectedChannel.id);
      }

      this.files.forEach((file) => {
        formData.append("files[]", file);
      });
      const hasPostFiles = this.files.length > 0;

      this.isSavingPost = true;
      store
          .dispatch(MODULE_NAME + "/" + CREATE_ITEM, {
            url: BASE_URL,
            contents: formData,
          })
          .then(() => {
            if (!this.error) {
              form.text = "";
              this.files = [];
              this.posts.data.unshift(this.post);
              this.getLatestActions();
            } else {
              this.errorMessages.push(
                  postCreationErrorMessage(this.error, hasPostFiles)
              );
              this.$store.commit(MODULE_NAME + "/" + SET_ERROR, null);
            }
          })
          .catch((error) => {
            this.errorMessages.push(
                postCreationErrorMessage(error, hasPostFiles)
            );
          })
          .finally(() => {
            this.isSavingPost = false;
          });
    },
    likePost(id, index) {
      this.$store
          .dispatch(REST_CREATE_ITEM, {
            url: this.likeURL + id,
            acceptPromise: true,
          })
          .then((result) => {
            this.posts.data[index].likes = result.data;
            this.posts.data[index].likes_count = result.data.length;
            this.posts.data[index].user_liked_count = this.posts.data[index]
                .user_liked_count
                ? 0
                : 1;
            this.getLatestActions();
          });
    },
    commentPost(id, index) {
      this.errorMessages = [];
      this.$store.commit(MODULE_NAME + "/" + SET_ERROR, null);

      let formData = new FormData();
      formData.append("text", this.form.comment);
      this.$store
          .dispatch(REST_CREATE_ITEM, {
            url: this.commentURL + id,
            contents: formData,
            acceptPromise: true,
          })
          .then((result) => {
            if (!this.error) {
              const newComment = result.data;

              this.posts.data[index].comments_count += 1;
              this.posts.data[index].comments.unshift(newComment);
              this.$set(this.visibleComments, id, true);

              this.form.comment = "";
              this.activeCommentPostId = null;
              this.getLatestActions();
            } else {
              this.errorMessages.push(this.error);
              this.$store.commit(MODULE_NAME + "/" + SET_ERROR, null);
            }
          });
    },
    sharePost(id) {
    },
    async openCommentArea(postId, index = null) {
      if (this.activeCommentPostId === postId) {
        this.activeCommentPostId = null;
      } else {
        this.activeCommentPostId = postId;
        const postIndex = index !== null ? index : this.posts.data.findIndex((p) => p.id === postId);
        if (postIndex > -1 && this.posts?.data?.[postIndex]) {
          await this.ensureCommentsLoaded(this.posts.data[postIndex], postIndex);
          this.$set(this.visibleComments, postId, true);
        }
        this.$nextTick(() => {
          const activeIndex = this.posts?.data?.findIndex((p) => p.id === postId);
          const textarea = this.$refs[`comment_textarea_${postId}_${activeIndex}`];
          if (textarea && textarea.length) {
            textarea[0].focus();
          }
        });
      }
    },
    getVisibleComments(post) {
      if (!post) return [];
      const comments = Array.isArray(post.comments) ? post.comments : [];
      if (this.isCommentsExpanded(post.id)) {
        return comments;
      }
      return comments.slice(0, 2);
    },
    hasMoreComments(post) {
      return this.getCommentsTotal(post) > 2;
    },
    isCommentsExpanded(postId) {
      return !!this.expandedComments[postId];
    },
    isCommentsVisible(postId) {
      return !!this.visibleComments[postId];
    },
    getCommentsTotal(post) {
      if (!post) return 0;
      const loadedCount = Array.isArray(post.comments) ? post.comments.length : 0;
      const counted = Number(post.comments_count) || 0;
      if (loadedCount > 0) {
        return loadedCount;
      }
      return counted;
    },
    shouldShowCommentsToggle(post) {
      return this.getCommentsTotal(post) > 2;
    },
    async ensureCommentsLoaded(post, index) {
      const hasLoaded = Array.isArray(post.comments) && post.comments.length > 0;
      if (hasLoaded || !post?.id) return;

      try {
        const response = await axios.get(`api/social-post/${post.id}`);
        const postDetail = response?.data || {};
        const comments =
            postDetail.comments ||
            postDetail.approved_comments ||
            postDetail.post_comments ||
            [];

        if (this.posts?.data?.[index]) {
          this.$set(this.posts.data[index], "comments", Array.isArray(comments) ? comments : []);
        }
      } catch (_) {
      }
    },
    async toggleComments(post, index) {
      if (!post?.id) return;
      const wasExpanded = this.isCommentsExpanded(post.id);
      if (!wasExpanded) {
        await this.ensureCommentsLoaded(post, index);
      }
      const nextExpanded = !wasExpanded;
      this.$set(this.expandedComments, post.id, nextExpanded);

      if (!nextExpanded) {
        this.scrollToPost(post.id);
      }
    },
    scrollToPost(postId) {
      this.$nextTick(() => {
        const postEl = document.getElementById(`wall-post-${postId}`);
        if (!postEl) return;

        const top = postEl.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: "smooth",
        });
      });
    },
    async toggleCommentsVisibility(post, index) {
      if (!post?.id) return;

      if (!this.isCommentsVisible(post.id)) {
        await this.ensureCommentsLoaded(post, index);
      }

      const nextVisible = !this.isCommentsVisible(post.id);
      this.$set(this.visibleComments, post.id, nextVisible);

      if (!nextVisible) {
        this.$set(this.expandedComments, post.id, false);
      }
    },
    findCommentInTree(comments, commentId) {
      if (!comments || !comments.length) return null;

      for (let comment of comments) {
        if (comment.id === commentId) {
          return comment;
        }
        if (comment.approved_replies && comment.approved_replies.length > 0) {
          const found = this.findCommentInTree(comment.approved_replies, commentId);
          if (found) return found;
        }
      }
      return null;
    },
    updateCommentInTree(comments, commentId, updatedComment) {
      if (!comments || !comments.length) return false;

      for (let i = 0; i < comments.length; i++) {
        if (comments[i].id === commentId) {
          this.$set(comments, i, updatedComment);
          return true;
        }
        if (comments[i].approved_replies && comments[i].approved_replies.length > 0) {
          const updated = this.updateCommentInTree(comments[i].approved_replies, commentId, updatedComment);
          if (updated) return true;
        }
      }
      return false;
    },
    replyComment(data) {
      let formData = new FormData();
      formData.append("text", data.text);
      formData.append("parent_id", data.comment_id);
      this.$store
          .dispatch(REST_CREATE_ITEM, {
            url: this.commentURL + data.id + "/reply/" + data.comment_id,
            contents: formData,
            acceptPromise: true,
          })
          .then((result) => {
            if (!this.error) {
              let updatedComments = result.data;

              const ensureUserObject = (comments) => {
                if (!comments || !comments.length) return;
                comments.forEach(comment => {
                  if (!comment.user && this.current_user) {
                    comment.user = {
                      id: this.current_user.id,
                      name: this.current_user.name,
                      surname: this.current_user.surname,
                      photo_link: this.current_user.photo_link
                    };
                  }
                  if (comment.approved_replies && comment.approved_replies.length > 0) {
                    ensureUserObject(comment.approved_replies);
                  }
                });
              };
              ensureUserObject(updatedComments);

              const updatedComment = this.findCommentInTree(updatedComments, data.comment_id);

              if (updatedComment) {
                this.updateCommentInTree(
                    this.posts.data[data.index].comments,
                    data.comment_id,
                    updatedComment
                );

                this.posts.data[data.index].comments_count += 1;
              } else {
                this.resetSocialPosts();
              }

              this.getLatestActions();
            } else this.errorMessages.push(this.error);
          });
    },
    handlePostMediaSelection(event) {
      const selectedFiles = Array.from(event?.target?.files || []);
      if (event?.target) event.target.value = "";
      if (!selectedFiles.length) return;

      const selectedImageFiles = selectedFiles.filter((file) =>
          file?.type?.startsWith("image/")
      );
      const selectedNonImageFiles = selectedFiles.filter(
          (file) => !file?.type?.startsWith("image/")
      );

      this.files.push(...selectedNonImageFiles);
      this.pendingImageCropFiles.push(...selectedImageFiles);
      this.openNextImageForCropping();
    },
    createImageCropPreviewUrl(file) {
      const urlApi = typeof window !== "undefined" ? window.URL : null;
      return urlApi?.createObjectURL ? urlApi.createObjectURL(file) : null;
    },
    openNextImageForCropping() {
      if (
          this.activeImageCropFile ||
          this.isApplyingImageCrop ||
          !this.pendingImageCropFiles.length
      ) {
        return;
      }

      const sourceImageFile = this.pendingImageCropFiles.shift();
      const previewUrl = this.createImageCropPreviewUrl(sourceImageFile);
      if (!previewUrl) {
        this.files.push(sourceImageFile);
        this.openNextImageForCropping();
        return;
      }

      const cropSessionId = ++this.imageCropSessionId;
      this.activeImageCropFile = sourceImageFile;
      this.activeImageCropPreviewUrl = previewUrl;
      this.activeImageNaturalWidth = 0;
      this.activeImageNaturalHeight = 0;
      this.loadActiveImageDimensions(previewUrl);

      this.$nextTick(() => {
        if (
            cropSessionId !== this.imageCropSessionId ||
            this.activeImageCropFile !== sourceImageFile
        ) {
          return;
        }

        this.$refs.imageRatioModal?.reset();
        this.isImageRatioModalVisible = true;
      });
    },
    loadActiveImageDimensions(previewUrl) {
      const ImageConstructor = typeof window !== "undefined" ? window.Image : null;
      if (!previewUrl || !ImageConstructor) return;

      const image = new ImageConstructor();
      image.onload = () => {
        if (this.activeImageCropPreviewUrl !== previewUrl) return;
        this.activeImageNaturalWidth = image.naturalWidth || image.width || 0;
        this.activeImageNaturalHeight = image.naturalHeight || image.height || 0;
      };
      image.onerror = () => {
        if (this.activeImageCropPreviewUrl !== previewUrl) return;
        this.activeImageNaturalWidth = 0;
        this.activeImageNaturalHeight = 0;
      };
      image.src = previewUrl;
    },
    releaseImageCropPreviewUrl() {
      const urlApi = typeof window !== "undefined" ? window.URL : null;
      if (this.activeImageCropPreviewUrl && urlApi?.revokeObjectURL) {
        urlApi.revokeObjectURL(this.activeImageCropPreviewUrl);
      }
      this.activeImageCropPreviewUrl = null;
    },
    resetActiveImageCrop() {
      this.imageCropSessionId += 1;
      this.activeImageCropFile = null;
      this.activeImageNaturalWidth = 0;
      this.activeImageNaturalHeight = 0;
      this.isApplyingImageCrop = false;
      this.releaseImageCropPreviewUrl();
      this.$refs.imageRatioModal?.reset();
    },
    cancelImageCropping() {
      if (this.isApplyingImageCrop) return;
      this.pendingImageCropFiles = [];
      this.resetActiveImageCrop();
      this.isImageRatioModalVisible = false;
    },
    completeActiveImageCrop(croppedImageFile) {
      if (croppedImageFile) this.files.push(croppedImageFile);
      this.resetActiveImageCrop();

      if (this.pendingImageCropFiles.length) {
        this.openNextImageForCropping();
      } else {
        this.isImageRatioModalVisible = false;
      }
    },
    keepOriginalImage() {
      if (!this.activeImageCropFile || this.isApplyingImageCrop) return;
      this.completeActiveImageCrop(this.activeImageCropFile);
    },
    async applySelectedImageAspect(aspectSelection) {
      if (!this.activeImageCropFile || this.isApplyingImageCrop) return;

      const sourceImageFile = this.activeImageCropFile;
      const cropSessionId = this.imageCropSessionId;
      const selectedAspectRatio =
          IMAGE_ASPECT_RATIOS[aspectSelection?.selectedAspect];
      if (!selectedAspectRatio) {
        this.completeActiveImageCrop(sourceImageFile);
        return;
      }

      this.isApplyingImageCrop = true;
      let croppedImageFile = sourceImageFile;
      try {
        croppedImageFile =
            (await cropImageByAspect(
                sourceImageFile,
                selectedAspectRatio,
                aspectSelection?.anchorX,
                aspectSelection?.anchorY
            )) || sourceImageFile;
      } catch {
        croppedImageFile = sourceImageFile;
      }

      if (
          cropSessionId !== this.imageCropSessionId ||
          this.activeImageCropFile !== sourceImageFile
      ) {
        return;
      }

      this.isApplyingImageCrop = false;
      this.completeActiveImageCrop(croppedImageFile);
    },
    removeFile(index) {
      let fileListArr = Array.from(this.files);
      fileListArr.splice(index, 1);
      this.files = fileListArr.length > 0 ? fileListArr : [];
    },
    bytesToSize(bytes) {
      let sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      if (bytes == 0) return "0 Byte";
      let i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
      return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
    },
    resetMessages() {
      this.errorMessages = [];
      this.successMessage = null;
    },
    timeFromNow(date) {
      return moment(date)
          .locale(this.activeLanguageCode)
          .format("DD MMMM YYYY");
    },
    setPage(page) {
      this.filter.page = page;
      this.filterSocialPosts();
    },

    getChannels() {
      this.$store
          .dispatch(REST_GET_ITEMS, {
            url: this.channelListURL,
            acceptPromise: true,
          })
          .then((response) => {
            if (Array.isArray(response.data)) {
              this.channels = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
              this.channels = response.data.data;
            }

            if (this.selectedChannel && this.isChannelPending(this.selectedChannel)) {
              this.clearChannel();
            }
          })
          .catch(() => {
          });
    },
    getUsers() {
      if (this.usersLoading) return;
      this.usersLoading = true;
      this.$store
          .dispatch(REST_GET_ITEMS, {
            url: this.usersListURL,
            acceptPromise: true,
          })
          .then((response) => {
            if (Array.isArray(response.data)) {
              this.users = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
              this.users = response.data.data;
            }
          })
          .catch(() => {
          })
          .finally(() => {
            this.usersLoading = false;
          });
    },
    async fetchMemberCriterionValues() {
      const endpoint = this.getMemberCriterionEndpointByType(this.memberCriterionType);
      if (!endpoint) {
        this.memberCriterionValues = [];
        return;
      }

      this.memberCriterionLoading = true;
      try {
        const response = await axios.get(endpoint, { params: { all: "all" } });
        if (Array.isArray(response.data)) {
          this.memberCriterionValues = response.data;
        } else if (Array.isArray(response.data?.data)) {
          this.memberCriterionValues = response.data.data;
        } else {
          this.memberCriterionValues = [];
        }
      } catch (_) {
        this.memberCriterionValues = [];
      } finally {
        this.memberCriterionLoading = false;
      }
    },
    getUserCriterionValues(user, type) {
      if (!user || !type) return [];

      const { idKeys, relationKeys } = this.getUserCriterionKeys(type);

      const values = [];
      idKeys.forEach((key) => {
        if (user[key] !== undefined && user[key] !== null && user[key] !== "") {
          values.push(String(user[key]));
        }
      });

      relationKeys.forEach((key) => {
        const relationValue = user[key];
        if (Array.isArray(relationValue)) {
          relationValue.forEach((item) => {
            if (item?.id !== undefined && item?.id !== null) {
              values.push(String(item.id));
            }
          });
        } else if (relationValue && relationValue.id !== undefined && relationValue.id !== null) {
          values.push(String(relationValue.id));
        }
      });

      return [...new Set(values)];
    },
    handleCriterionTypeChange(type) {
      this.memberCriterionType = type;
      this.memberCriterionValue = null;
      this.fetchMemberCriterionValues();
    },
    handleCriterionValueChange(value) {
      this.memberCriterionValue = value;
    },
    handleApplyCriterion(value) {
      if (!value) return;
      const matches = this.criterionMatchedUsers;
      if (!matches.length) return;

      matches.forEach((user) => {
        const alreadySelected = this.newChannel.selected_members.some(
            (m) => String(m.id) === String(user.id)
        );
        if (!alreadySelected) {
          this.newChannel.selected_members.push(user);
        }
      });

      if (this.$refs.channelModal) {
        this.$refs.channelModal.channelData.selected_members = [...this.newChannel.selected_members];
      }
    },
    handleSaveChannel(data) {
      this.newChannel.name = data.name;
      this.newChannel.is_private = data.is_private;
      this.newChannel.selected_members = data.selected_members;
      this.saveNewChannel();
    },
    goToFullWall() {
      this.$router.push({ name: 'user.wall' });
      this.filter.page = 1;
      this.filter.search = null;
      this.filterSocialPosts();
    },
    selectChannel(channel) {
      if (this.isChannelPending(channel)) {
        return;
      }

      // Tüm Kanallar
      if (channel.id === null) {
        this.clearChannel();
        return;
      }

      if (this.isChannelSelected(channel)) {
        this.clearChannel();
        return;
      }

      this.selectedChannel = channel;
      this.filter.search = null;
      this.filter.page = 1;
      this.filterSocialPosts();
    },
    isChannelSelected(channel) {
      if (channel.id === null) {
        return !this.selectedChannel;
      }
      if (!this.selectedChannel) return false;
      const key = channel.id || channel.slug || channel.name;
      const selectedKey =
          this.selectedChannel.id ||
          this.selectedChannel.slug ||
          this.selectedChannel.name;

      return key === selectedKey;
    },
    isChannelNew(channel) {
      const createdAt = channel?.created_at || channel?.createdAt || channel?.inserted_at;
      if (!createdAt) return false;

      const createdMoment = moment(createdAt);
      if (!createdMoment.isValid()) return false;

      return createdMoment.isBetween(moment().subtract(7, "days"), moment(), null, "[]");
    },
    clearChannel() {
      this.selectedChannel = null;
      this.filter.search = null;
      this.filter.page = 1;
      this.filterSocialPosts();
    },
    resetChannelModalState() {
      this.editingChannel = null;
      this.isSavingChannel = false;
      this.newChannel.name = "";
      this.newChannel.is_private = false;
      this.newChannel.selected_members = [];
      this.memberCriterionType = "position";
      this.memberCriterionValue = null;
      this.memberCriterionValues = [];
      if (this.$refs.channelModal) {
        this.$refs.channelModal.reset();
      }
    },
    onChannelModalHidden() {
      this.channelEditRequestKey += 1;
      this.resetChannelModalState();
    },
    openNewChannelModal() {
      this.channelEditRequestKey += 1;
      this.resetChannelModalState();
      UIkit.modal("#modal_channel_new").show();
      this.fetchMemberCriterionValues();
    },
    editChannel(channel) {
      const requestKey = ++this.channelEditRequestKey;
      this.resetChannelModalState();
      this.newChannel.name = channel.name || channel.title || "";
      this.newChannel.is_private = channel.is_private || channel.type === 2 || false;

      if (this.$refs.channelModal) {
        this.$refs.channelModal.setChannelData({
          name: this.newChannel.name,
          is_private: this.newChannel.is_private,
          selected_members: [],
        });
      }

      UIkit.modal("#modal_channel_new").show();
      this.fetchMemberCriterionValues();

      axios.get(`api/social/channels/${channel.id}`)
          .then((response) => {
            if (requestKey !== this.channelEditRequestKey) return;
            const channelDetail = response.data;

            const channelMembers = channelDetail.members ||
                channelDetail.channel_users ||
                channelDetail.channel_members ||
                channelDetail.users ||
                [];

            this.editingChannel = channelDetail;
            this.newChannel.selected_members = [...channelMembers];

            if (this.$refs.channelModal) {
              this.$refs.channelModal.setChannelData({
                name: this.newChannel.name,
                is_private: this.newChannel.is_private,
                selected_members: [...channelMembers],
              });
            }
          })
          .catch(() => {
            if (requestKey !== this.channelEditRequestKey) return;
            this.newChannel.selected_members = [];
          });
    },
    deleteChannel(channel) {
      confirmDialogDelete({
        context: this,
        message: this.$t('general.are_you_sure')
      }).then((confirmed) => {
        if (!confirmed) return;

        const channelId = channel.id;

        axios.delete(`api/social/channels/${channelId}`)
            .then(() => {
              const index = this.channels.findIndex(c => c.id === channelId);
              if (index > -1) {
                this.channels.splice(index, 1);
              }
              if (this.selectedChannel?.id === channelId) {
                this.clearChannel();
              }

              this.filterSocialPosts();

              Swal.fire({
                icon: 'success',
                text: this.$t('general.successfully_deleted'),
                timer: 2000,
                showConfirmButton: false
              });
            })
            .catch(() => {
              Swal.fire({
                icon: 'error',
                text: this.$t('general.error_occurred')
              });
            });
      });
    },
    getChannelStatusId(channelData) {
      const raw =
          channelData?.status_id ??
          channelData?.statusId ??
          channelData?.channel_status_id;
      const parsed = Number(raw);

      if (raw === undefined || raw === null || raw === "") {
        return 2;
      }
      return Number.isNaN(parsed) ? 2 : parsed;
    },
    isChannelApproved(channelData) {
      return this.getChannelStatusId(channelData) === 2;
    },
    isChannelPending(channelData) {
      return this.getChannelStatusId(channelData) === 1;
    },
    saveNewChannel() {
      if (this.isSavingChannel) return;
      const name = (this.newChannel.name || "").trim().replace(/\s+/g, '');
      if (!name) return;
      this.isSavingChannel = true;

      const isEditing = !!this.editingChannel;
      const channelId = isEditing ? this.editingChannel.id : null;

      const payload = {
        name: name,
        type: this.newChannel.is_private ? 2 : 1
      };

      if (!isEditing && !this.isCompanyAdmin) {
        payload.status_id = 1;
      }

      if (!isEditing && this.newChannel.is_private && this.newChannel.selected_members && this.newChannel.selected_members.length) {
        payload.member_ids = this.newChannel.selected_members.map(m => m.id);
      }

      const request = isEditing
          ? axios.put(`api/social/channels/${channelId}`, payload)
          : axios.post(this.channelCreateURL, payload);

      request
          .then((response) => {
            const channelData = response?.data?.data || response.data;

            if (isEditing && this.newChannel.is_private) {
              const originalMembers = this.editingChannel.members || [];

              const originalMemberIds = originalMembers.map(m => m.id);
              const newMemberIds = this.newChannel.selected_members.map(m => m.id);
              const removedMemberIds = originalMemberIds.filter(id => !newMemberIds.includes(id));
              const addedMemberIds = newMemberIds.filter(id => !originalMemberIds.includes(id));
              const memberUpdatePromises = [];

              removedMemberIds.forEach(userId => {
                memberUpdatePromises.push(
                    axios.delete(`api/social/channels/${channelId}/members/${userId}`)
                );
              });

              if (addedMemberIds.length > 0) {
                memberUpdatePromises.push(
                    axios.post(`api/social/channels/${channelId}/members`, {
                      user_ids: addedMemberIds
                    })
                );
              }

              Promise.all(memberUpdatePromises)
                  .then(() => {
                    return axios.get(`api/social/channels/${channelId}`);
                  })
                  .then((detailResponse) => {
                    const updatedChannel = detailResponse.data;

                    const index = this.channels.findIndex(c => c.id === channelId);
                    if (index > -1) {
                      this.channels.splice(index, 1, updatedChannel);
                    }
                    this.isSavingChannel = false;
                    UIkit.modal("#modal_channel_new").hide();
                  })
                  .catch(() => {
                    this.isSavingChannel = false;
                    Swal.fire({
                      icon: 'warning',
                      title: this.$t('general.warning'),
                      text: this.$t('general.channel_updated_but_members_failed'),
                      confirmButtonText: this.$t('general.ok')
                    }).then(() => {
                      UIkit.modal("#modal_channel_new").hide();
                    });
                  });
            } else {
              if (!isEditing) {
                if (this.isChannelPending(channelData)) {
                  this.getChannels();
                  Swal.fire({
                    icon: 'success',
                    text: this.$t('general.sent_to_admin_approval'),
                    timer: 2200,
                    showConfirmButton: false
                  });
                } else {
                  this.channels.unshift(channelData);
                }
              } else {
                const index = this.channels.findIndex(c => c.id === channelId);
                if (index > -1) {
                  this.channels.splice(index, 1, channelData);
                }
              }
              this.isSavingChannel = false;
              UIkit.modal("#modal_channel_new").hide();
            }
          })
          .catch((error) => {
            this.isSavingChannel = false;
            const errorData = error.response?.data;
            let errorMessage = errorData?.message || errorData?.error || 'Bir hata oluştu';

            if (errorMessage.includes('Duplicate entry') || errorMessage.includes('1062') || error.response?.status === 409) {
              errorMessage = this.$t('general.channel_already_exists');
            }

            Swal.fire({
              icon: 'error',
              title: this.$t('general.error'),
              text: errorMessage,
              confirmButtonText: this.$t('general.ok')
            }).then(() => {
              // Hata durumunda da modalı kapat
              UIkit.modal("#modal_channel_new").hide();
            });
          });
    },
  },
  mounted() {
    if (this.$route.params.search_text) {
      this.filter.search = this.$route.params.search_text;
      this.filterSocialPosts();
    }
    this.getUsers();
    if (!this.socialPostVisible) {
      this.$router.push({ name: "user.index" });
    }
    const data = ls.get("dashboardMenuList");
    if (data && data.menu) {
      this.menuList = data.menu;
    }
    this.userId = this.$route.params.user;
    this.getCurrentUser();
    this.filterSocialPosts(this.id);
    this.getLatestActions();
    this.getChannels();

    this.channelModalEl = document.getElementById("modal_channel_new");
    if (this.channelModalEl) {
      this.channelModalEl.addEventListener("hidden", this.onChannelModalHidden);
    }
    window.addEventListener("keydown", this.onImagePreviewKeydown);
  },
  beforeDestroy() {
    if (this.channelModalEl) {
      this.channelModalEl.removeEventListener("hidden", this.onChannelModalHidden);
    }
    window.removeEventListener("keydown", this.onImagePreviewKeydown);
    this.pendingImageCropFiles = [];
    this.resetActiveImageCrop();
  },
  watch: {
    successMessage() {
      this.debouncedResetValues();
    },
    errorMessages() {
      this.debouncedResetValues();
    },
    "filter.search"(val) {
      if (!val) {
        this.selectedChannel = null;
      }
    },
    memberCriterionType() {
      this.memberCriterionValue = null;
      this.fetchMemberCriterionValues();
    },
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.filter.page = 1;
          this.filterSocialPosts(newId);

          this.$nextTick(() => {
            setTimeout(() => {
              const postEl = document.getElementById(`wall-post-${newId}`);
              if (postEl) {
                const top = postEl.getBoundingClientRect().top + window.pageYOffset - 90;
                window.scrollTo({
                  top: Math.max(0, top),
                  behavior: "smooth",
                });
              }
            }, 500);
          });
        }
      }
    },
  },
  created() {
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>

<style scoped src="./Wall.css"></style>
